import { IconNames } from '@blueprintjs/icons';
import { BenefitTypeDisplayTextMap, RouteCodeDisplayTextMap } from '@strata/constants';
import { sortAsc } from '@strata/helper-functions';
import { ActionButtonText, ToggleButton } from '@strata/react-buttons';
import { GridWithSearch, PrimaryData, SecondaryData } from '@strata/react-grid';
import { Icon } from '@strata/react-status-icons';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ListPageHeader } from 'components/ListPageHeader';
import { RouteStatusIndicator } from 'pages/routeManagement/RouteStatusIndicator';
import { CREATE_ROUTE } from 'services/constants';
import { useGetWithRefetch } from 'services/Http';
import {
    globalRouteAppRoute,
    globalRouteSegmentUsageUrl,
    globalRoutesUrl,
} from 'services/RouteRuleHelpers';

/** A segment still routing on a global route: its placement there has not expired */
const isUsageActive = ({ start_date: startDate, end_date: endDate }) =>
    RouteStatusIndicator.getStatus({ startDate, endDate }).message !==
    RouteStatusIndicator.Statuses.INACTIVE;

const columns = ({ navigate }) => [
    {
        header: 'Route Rule Name',
        accessorKey: 'route_name',
        size: 200,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        header: 'Description',
        accessorKey: 'route_description',
        size: 250,
    },
    {
        header: 'Benefit',
        accessorKey: 'benefit',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={BenefitTypeDisplayTextMap[getValue()]} />,
    },
    {
        header: 'Route Code',
        accessorKey: 'route_code',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={RouteCodeDisplayTextMap[getValue()]} />,
    },
    {
        header: 'Segments Used',
        accessorKey: 'segment_count',
        size: 110,
    },
    {
        header: 'Payer Segments',
        accessorKey: 'segment_ids',
        size: 250,
        cell: ({ getValue }) => (
            <div className="sds_grid-cell--wrap">
                {/* The wrapped cell shows every segment, so a tooltip would only repeat it */}
                <SecondaryData data={getValue()} customTooltipContent={null} />
            </div>
        ),
    },
    {
        header: 'Actions',
        accessorKey: 'route_rule_id',
        size: 70,
        enableSorting: false,
        cell: ({ getValue }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={IconNames.EDIT}
                    title="Edit"
                    onClick={() => navigate(globalRouteAppRoute(getValue()))}
                />
            </div>
        ),
    },
];

export const GlobalRouteList = () => {
    const navigate = useNavigate();

    const { result: globalRoutes, refetch: refetchGlobalRoutes } =
        useGetWithRefetch(globalRoutesUrl);
    // Which segments use a global route is only known to the payer databases holding it
    const { result: segmentUsage, refetch: refetchSegmentUsage } = useGetWithRefetch(
        globalRouteSegmentUsageUrl,
    );

    const [hideInactiveRoutes, setHideInactiveRoutes] = useState(true);

    const [isRefreshing, setIsRefreshing] = useState(false);
    const handleRefreshClick = async () => {
        setIsRefreshing(true);
        // Each refetch reports its own failure, so the list stops loading either way
        try {
            await Promise.all([refetchGlobalRoutes(), refetchSegmentUsage()]);
        } finally {
            setIsRefreshing(false);
        }
    };

    const isLoading = isRefreshing || !(globalRoutes && segmentUsage);

    const globalRouteRows = isLoading
        ? undefined
        : globalRoutes.map((globalRoute) => {
              const segmentIds = segmentUsage
                  .filter(
                      (usage) =>
                          usage.route_rule_id === globalRoute.route_rule_id &&
                          (!hideInactiveRoutes || isUsageActive(usage)),
                  )
                  .map(({ segment_id: segmentId }) => segmentId)
                  .sort(sortAsc);

              return {
                  ...globalRoute,
                  segment_count: segmentIds.length,
                  segment_ids: segmentIds.join(', '),
              };
          });

    return (
        <div className="sds_list-page">
            <div className="sds_main-content__container">
                <ListPageHeader
                    pageTitle="Global Routes"
                    buttons={[
                        {
                            label: 'Create Global Route',
                            path: globalRouteAppRoute(CREATE_ROUTE),
                        },
                    ]}
                />
                <div className="sds_main-content__grid">
                    <GridWithSearch
                        data={globalRouteRows}
                        isLoading={isLoading}
                        sortProperties={[{ id: 'route_name', desc: false }]}
                        columns={columns({ navigate })}
                        rightHeaderContent={
                            <div className="flex__gap--medium">
                                <ToggleButton
                                    label="Hide Inactive Routes"
                                    isOn={hideInactiveRoutes}
                                    onChange={(e) => setHideInactiveRoutes(e.currentTarget.checked)}
                                />
                                <ActionButtonText
                                    leftIcon={IconNames.REFRESH}
                                    label="Refresh"
                                    disabled={isRefreshing}
                                    onClick={handleRefreshClick}
                                />
                            </div>
                        }
                    />
                </div>
            </div>
        </div>
    );
};
